/**
 * Tax returns tab in the Bookings layout (Fan, 2026-10-28: "exactly
 * formatted like the Bookings section ... with filters, dates, marked paid
 * or not paid. And with the attachment" + "the tax return tab should sum up
 * and explain the By entity tab").
 *
 * Pure grouping over GET /payroll/vat-returns rows, merged (Fan,
 * 2026-10-28: "the UK ones don't include the payment plans", "the 36k
 * outstanding VAT is missing", "the PAYE is missing") with the tax debts
 * (plans, notices, PAYE months from GET /payroll/overview) and the bank
 * payments/refunds to tax offices (GET /reports/tax-paid) that no return or
 * debt explains. Each row keeps its own currency; the table shows every
 * figure converted to the display currency (Fan, 2026-10-30), with the
 * row's own currency in a Currency column.
 */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const RETURN_STATUS = {
  paid: { label: 'Paid', tone: 'green' },
  unpaid: { label: 'Not paid', tone: 'red' },
  offset: { label: 'Set off by the tax office', tone: 'blue' },
  refunded: { label: 'Refunded to the bank', tone: 'green' },
  nil: { label: 'Nothing to pay', tone: 'grey' },
  draft: { label: 'Draft, filed version not received', tone: 'amber' },
  plan: { label: 'On a payment plan', tone: 'amber' },
  upcoming: { label: 'Due later', tone: 'grey' },
};

/** 'paid' | 'unpaid' | 'offset' | 'refunded' | 'nil' | 'draft' | 'plan' | 'upcoming'. */
export function returnStatus(r) {
  if (r?.status_key && RETURN_STATUS[r.status_key]) return r.status_key;
  const s = String(r?.settlement || '').toLowerCase();
  if (s === 'draft') return 'draft';
  if (r?.result === 'nil' || s === 'nothing to pay' || Number(r?.amount) === 0) return 'nil';
  if (s === 'paid' || s === 'offset' || s === 'refunded') return s;
  return 'unpaid';
}

/** Month the row belongs to: filed, else due, else period end (debts and
 * bank lines carry their own `month_date`). */
export function returnMonthKey(r) {
  return String(r?.month_date || r?.filed_date || r?.due_date || r?.period_end || '').slice(0, 7) || null;
}

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const dayDiff = (a, b) => Math.abs((new Date(String(a).slice(0, 10)) - new Date(String(b).slice(0, 10))) / 86400000);
const fmtDay = (d) => {
  const [y, m, dd] = String(d || '').slice(0, 10).split('-');
  return dd ? `${dd}/${m}/${y}` : '';
};

function debtStatus(o) {
  if (o.status === 'paid' || o.state === 'paid') return 'paid';
  if (o.state === 'overdue') return 'unpaid';
  if (o.status === 'payment_plan' || o.state === 'no_dates') return 'plan';
  if (o.state === 'due' || o.status === 'scheduled') return 'upcoming';
  return 'unpaid';
}

function debtForm(o) {
  if (o.kind === 'payroll_paye') return 'PAYE (P32)';
  if (o.status === 'payment_plan' || /\bplan\b/i.test(`${o.label || ''} ${o.tax_note || ''}`)) return 'Payment plan';
  return 'Notice';
}

/**
 * One list for the Tax returns tab:
 *  - every return (vat_returns),
 *  - every debt (tax_obligations, not cancelled) unless it IS a listed
 *    return (`return_id`), e.g. a payment plan, the HMRC arrears letter, a
 *    PAYE month, a plan instalment, a notice,
 *  - every bank payment / refund to a tax office that no row explains.
 * A bank line explains a row when the row lists it (`bank_tx_ids`) or, else,
 * when it is the same entity, currency and amount (to the cent) within 400
 * days; a returned direct debit and the debit it reverses cancel out.
 */
export function mergeTaxItems({
  returns = [], obligations = [], payments = [], refunds = [],
  nameOf = (id) => id, kindLabel = () => null, debtKindOf = () => 'other', kindOf = (k) => k,
} = {}) {
  const returnIds = new Set(returns.map((r) => r.id));
  const rows = returns.map((r) => ({ ...r, item_type: 'return', entity_name: r.entity_name || nameOf(r.entity_id) }));
  obligations.forEach((o) => {
    if (o.return_id && returnIds.has(o.return_id)) return;
    const status = debtStatus(o);
    const when = o.due_date || o.paid_on || o.notice_date || null;
    const bits = [];
    if (o.due_date) bits.push(`Due ${fmtDay(o.due_date)}`);
    else if (o.notice_date) bits.push(`Notice of ${fmtDay(o.notice_date)}`);
    if (o.paid_on) bits.push(`paid ${fmtDay(o.paid_on)}`);
    if (o.payment_method === 'direct_debit') bits.push('direct debit');
    if (num(o.original_amount) > num(o.amount)) bits.push(`originally ${num(o.original_amount).toFixed(2)} ${o.currency || ''}`.trim());
    rows.push({
      id: o.id,
      item_type: 'debt',
      entity_id: o.entity_id,
      entity_name: nameOf(o.entity_id),
      tax: kindLabel(debtKindOf(o)) || o.tax_type || 'Tax',
      form: debtForm(o),
      period_label: o.tax_note ? `${o.label}: ${o.tax_note}` : o.label,
      date_label: bits.join(', ') || 'No date',
      month_date: when,
      amount: o.amount,
      currency: o.currency,
      status_key: status,
      note: o.note || null,
      bank_tx_ids: o.bank_tx_ids || [],
    });
  });

  // Bank lines: drop returned direct debits with the debit they reverse.
  const bank = payments.filter((ln) => ln.role !== 'reversal').map((ln) => ({ ...ln, used: false }));
  payments.filter((ln) => ln.role === 'reversal').forEach((rev) => {
    const hit = bank.find((ln) => !ln.used && ln.entity_id === rev.entity_id && ln.authority === rev.authority
      && Math.abs(Math.abs(num(ln.amount)) - Math.abs(num(rev.amount))) < 0.005 && String(ln.date) <= String(rev.date));
    if (hit) hit.used = true;
  });
  const back = refunds.map((ln) => ({ ...ln, used: false }));
  const byTx = new Map();
  [...bank, ...back].forEach((ln) => { if (ln.transaction_id) byTx.set(ln.transaction_id, ln); });

  rows.forEach((row) => {
    const linked = [];
    (row.bank_tx_ids || []).forEach((id) => {
      const ln = byTx.get(id);
      if (ln && !ln.used) { ln.used = true; linked.push(ln); }
    });
    const amt = num(row.amount);
    if (!linked.length && amt > 0 && returnStatus(row) !== 'nil') {
      const anchor = row.month_date || row.filed_date || row.due_date || row.period_end;
      const cands = bank.filter((ln) => !ln.used && ln.entity_id === row.entity_id
        && String(ln.currency || '').toUpperCase() === String(row.currency || '').toUpperCase()
        && Math.abs(Math.abs(num(ln.amount)) - amt) < 0.005
        && (!anchor || dayDiff(ln.date, anchor) <= 400));
      cands.sort((a, b) => (anchor ? dayDiff(a.date, anchor) - dayDiff(b.date, anchor) : 0));
      if (cands[0]) { cands[0].used = true; linked.push(cands[0]); }
    }
    if (!linked.length) return;
    row.bank_lines = linked.map((ln) => ({ date: ln.date, amount: num(ln.amount), currency: ln.currency }));
    const out = linked.filter((ln) => num(ln.amount) < 0).reduce((s, ln) => s - num(ln.amount), 0);
    const st = returnStatus(row);
    if (amt > 0 && out + 0.005 >= amt && ['unpaid', 'draft', 'plan', 'upcoming'].includes(st)) {
      row.status_key = 'paid';
    }
  });

  bank.filter((ln) => !ln.used).forEach((ln) => rows.push({
    id: `bank_${ln.transaction_id || `${ln.entity_id}_${ln.date}_${ln.amount}`}`,
    item_type: 'payment',
    entity_id: ln.entity_id,
    entity_name: nameOf(ln.entity_id),
    tax: kindLabel(kindOf(ln.kind)) || ln.kind_label || 'Tax',
    form: 'Bank payment',
    period_label: ln.tax_note ? `Paid to ${ln.authority}: ${ln.tax_note}` : `Paid to ${ln.authority}`,
    date_label: ln.tax_note ? `Paid ${fmtDay(ln.date)}` : `Paid ${fmtDay(ln.date)}, no return or notice on file`,
    month_date: ln.date,
    amount: Math.abs(num(ln.amount)),
    currency: ln.currency,
    status_key: 'paid',
    note: ln.label ? String(ln.label).replace(/\s+/g, ' ').slice(0, 110) : null,
  }));
  back.filter((ln) => !ln.used).forEach((ln) => rows.push({
    id: `refund_${ln.transaction_id || `${ln.entity_id}_${ln.date}_${ln.amount}`}`,
    item_type: 'refund',
    entity_id: ln.entity_id,
    entity_name: nameOf(ln.entity_id),
    tax: kindLabel(kindOf(ln.kind)) || ln.kind_label || 'Tax',
    form: 'Refund',
    period_label: `Refund from ${ln.authority}`,
    date_label: `Received ${fmtDay(ln.date)}, no return on file`,
    month_date: ln.date,
    amount: -Math.abs(num(ln.amount)),
    currency: ln.currency,
    status_key: 'refunded',
    note: ln.label ? String(ln.label).replace(/\s+/g, ' ').slice(0, 110) : null,
  }));
  return rows;
}

function add(map, ccy, v) {
  if (!v) return;
  const k = ccy || 'GBP';
  map[k] = (map[k] || 0) + v;
}

function blankSums() {
  return { toPay: {}, refund: {}, paid: {}, notPaid: {} };
}

/**
 * Adds one row to the sums. With `conv` (Fan, 2026-10-30: "just have one
 * currency according to the currency toggle ... each column one number
 * max") every amount is converted to the display currency and summed under
 * the single key DISPLAY_KEY; a row with no rate is counted in
 * `sums.unconverted` and left out rather than mixed in unconverted.
 */
export const DISPLAY_KEY = 'display';

function addReturn(sums, r, conv) {
  const raw = Number(r.amount) || 0;
  const st = returnStatus(r);
  if (st === 'nil') return;
  let amt = raw;
  let ccy = r.currency;
  if (conv && raw) {
    const v = conv(Math.abs(raw), r.currency);
    if (v === null || v === undefined || !Number.isFinite(v)) {
      sums.unconverted = (sums.unconverted || 0) + 1;
      return;
    }
    amt = raw < 0 ? -v : v;
    ccy = DISPLAY_KEY;
  }
  if (amt > 0) {
    add(sums.toPay, ccy, amt);
    if (st === 'paid') add(sums.paid, ccy, amt);
    else add(sums.notPaid, ccy, amt);
  } else if (amt < 0) {
    add(sums.refund, ccy, -amt);
  }
}

export function filterOptions(rows) {
  const uniq = (f) => [...new Set(rows.map(f).filter(Boolean))].sort();
  return {
    companies: uniq((r) => r.entity_name || r.entity_id),
    taxes: uniq((r) => r.tax || 'VAT'),
    statuses: Object.keys(RETURN_STATUS).filter((k) => rows.some((r) => returnStatus(r) === k)),
  };
}

export function applyFilters(rows, { companies = [], taxes = [], statuses = [] } = {}) {
  return rows.filter((r) => (!companies.length || companies.includes(r.entity_name || r.entity_id))
    && (!taxes.length || taxes.includes(r.tax || 'VAT'))
    && (!statuses.length || statuses.includes(returnStatus(r))));
}

/**
 * Month groups for one year, Bookings style: every month up to now (all 12
 * for a past year), plus any later month that has a return (due dates).
 * `conv(amount, currency)` -> display-currency number (or null) sums every
 * column into one figure under DISPLAY_KEY; without it sums stay per currency.
 */
export function buildReturnGroups(rows, year, today = new Date(), conv = null) {
  const y = Number(year);
  const lastMonth = y < today.getFullYear() ? 12 : y > today.getFullYear() ? 0 : today.getMonth() + 1;
  const groups = MONTHS.map((label, i) => ({ month: i + 1, label: `${label} ${y}`, returns: [], ...blankSums() }));
  const undated = { month: 0, label: 'Open, no date', returns: [], ...blankSums() };
  const totals = { count: 0, ...blankSums() };
  rows.forEach((r) => {
    const key = returnMonthKey(r);
    // An open debt with no date (plan with no schedule yet) is still owed:
    // shown in every year's view on its own row, never dropped.
    const g = !key ? undated : (Number(key.slice(0, 4)) === y ? groups[Number(key.slice(5, 7)) - 1] : null);
    if (!g) return;
    g.returns.push(r);
    addReturn(g, r, conv);
    addReturn(totals, r, conv);
    totals.count += 1;
  });
  const day = (r) => String(r.filed_date || r.due_date || r.period_end || '');
  groups.forEach((g) => g.returns.sort((a, b) => day(a).localeCompare(day(b))));
  const shown = groups.filter((g) => g.month <= lastMonth || g.returns.length > 0);
  return {
    groups: undated.returns.length ? [undated, ...shown] : shown,
    totals,
  };
}

const addMonth = (d) => {
  const [y, m, dd] = String(d || '').slice(0, 10).split('-').map(Number);
  if (!y) return null;
  const nm = m === 12 ? 1 : m + 1;
  const ny = m === 12 ? y + 1 : y;
  return `${ny}-${String(nm).padStart(2, '0')}-${String(Math.min(dd, 28)).padStart(2, '0')}`;
};
const cents = (v) => Math.round(Math.abs(num(v)) * 100);

/**
 * Payment plans table (Fan, 2026-10-28: "put like a table in place where
 * the payment plans are shown"). One row per plan:
 *  - an instalment series (obligations labelled "..., instalment N"),
 *  - a debt on a payment plan (status payment_plan; schedule may be unknown),
 *  - a monthly direct debit to a tax office with no debt behind it
 *    (3+ bank payments, same entity, office and amount), e.g. BI UK
 *    "Hmrc Ndds". Its description comes from the bank line's tax_note.
 * Amounts are native currency; null means "not on file".
 */
export function buildPaymentPlans({ obligations = [], payments = [], nameOf = (id) => id } = {}) {
  const plans = [];
  const live = obligations.filter((o) => o.status !== 'cancelled' && o.state !== 'cancelled');
  const series = new Map();
  live.forEach((o) => {
    if (!/,?\s*instalment\s+\d+/i.test(o.label || '')) return;
    const key = `${o.entity_id}|${o.authority}|${o.tax_note || String(o.label).replace(/,?\s*instalment\s+\d+/i, '')}`;
    if (!series.has(key)) series.set(key, []);
    series.get(key).push(o);
  });
  const seriesAmounts = new Set();
  series.forEach((items) => {
    items.sort((a, b) => String(a.due_date || '').localeCompare(String(b.due_date || '')));
    const first = items[0];
    const isPaid = (o) => o.status === 'paid' || o.state === 'paid';
    const open = items.filter((o) => !isPaid(o));
    const agreed = items.reduce((s, o) => s + num(o.amount), 0);
    const left = open.reduce((s, o) => s + num(o.amount), 0);
    const inst = num((open[0] || first).amount);
    seriesAmounts.add(`${first.entity_id}|${cents(inst)}`);
    const withDocs = items.find((o) => (o.documents || []).length);
    plans.push({
      documents: withDocs ? withDocs.documents : [],
      doc_ob_id: withDocs ? withDocs.id : null,
      id: `series_${first.id}`,
      entity_id: first.entity_id,
      entity_name: nameOf(first.entity_id),
      authority: first.authority,
      covers: first.tax_note || String(first.label).replace(/,?\s*instalment\s+\d+/i, ''),
      currency: first.currency,
      agreed,
      paid: agreed - left,
      left,
      instalment: inst,
      instalments: `${items.length - open.length} of ${items.length} paid`,
      method: first.payment_method === 'direct_debit' ? 'Direct debit' : null,
      next_date: open[0]?.due_date || null,
      end_date: items[items.length - 1].due_date || null,
      start_date: first.due_date || null,
      status: open.length ? 'running' : 'done',
    });
  });
  live.forEach((o) => {
    if (o.status !== 'payment_plan' || /,?\s*instalment\s+\d+/i.test(o.label || '')) return;
    const orig = num(o.original_amount) > num(o.amount) ? num(o.original_amount) : num(o.amount);
    plans.push({
      documents: o.documents || [],
      doc_ob_id: o.id,
      id: o.id,
      entity_id: o.entity_id,
      entity_name: nameOf(o.entity_id),
      authority: o.authority,
      covers: o.tax_note || o.tax_type || o.label,
      currency: o.currency,
      agreed: orig,
      paid: orig - num(o.amount),
      left: num(o.amount),
      instalment: null,
      instalments: o.schedule_known ? null : 'Schedule not on file',
      method: o.payment_method === 'direct_debit' ? 'Direct debit' : null,
      next_date: o.due_date || null,
      end_date: null,
      start_date: o.notice_date || null,
      status: 'running',
    });
  });
  const groups = new Map();
  payments.filter((ln) => ln.role === 'payment' && num(ln.amount) < 0).forEach((ln) => {
    const key = `${ln.entity_id}|${ln.authority}|${String(ln.currency || '').toUpperCase()}|${cents(ln.amount)}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(ln);
  });
  groups.forEach((lines) => {
    const months = new Set(lines.map((ln) => String(ln.date).slice(0, 7)));
    if (months.size < 3) return;
    const first = lines[0];
    if (seriesAmounts.has(`${first.entity_id}|${cents(first.amount)}`)) return;
    lines.sort((a, b) => String(a.date).localeCompare(String(b.date)));
    const last = lines[lines.length - 1];
    const note = lines.map((ln) => ln.tax_note).find(Boolean);
    const paid = lines.reduce((s, ln) => s + Math.abs(num(ln.amount)), 0);
    plans.push({
      id: `bank_${first.entity_id}_${first.authority}_${cents(first.amount)}`,
      entity_id: first.entity_id,
      entity_name: nameOf(first.entity_id),
      authority: first.authority,
      covers: note || `Monthly payment to ${first.authority}, no plan letter on file`,
      currency: String(first.currency || '').toUpperCase(),
      agreed: null,
      paid,
      left: null,
      instalment: Math.abs(num(first.amount)),
      instalments: `${lines.length} paid since ${fmtDay(first.date)}`,
      method: 'Direct debit',
      next_date: addMonth(last.date),
      next_estimated: true,
      end_date: null,
      start_date: String(first.date).slice(0, 10),
      status: 'running',
    });
  });
  return plans.sort((a, b) => (b.left ?? 0) - (a.left ?? 0) || String(a.entity_name).localeCompare(String(b.entity_name)));
}
